'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { BookOpen, ChevronRight, Sparkles, Loader2, RefreshCw, Trash2, LogIn } from 'lucide-react';
import { useAuthStore } from '@/lib/store/auth';
import {
  listStages,
  getFirstSlideByStages,
  type StageListItem,
} from '@/lib/storage-bridge/stage-storage-v2';
import { ThumbnailSlide } from '@/components/slide-renderer/components/ThumbnailSlide';
import type { Slide } from '@/lib/types/slides';

interface LessonStatus {
  lesson_index: number;
  status: number; // 0=未学, 1=进行中, 2=已完成
  progress?: number; // 0-100 百分比，来自 lesson_progress 表
  // 【dongniai】课时标题（来自大纲）
  lesson_title?: string;
  // 异步生成时课时时长保存在 lesson_progress.lessons
  lesson_hours?: number;
}

interface CourseOutline {
  lesson: string;
  title: string;
  hours: number;
  description: string;
}

interface Course {
  id: number;
  subject: string;
  education_level: string;
  education_grade: string;
  current_level: string;
  goal: string;
  total_hours: number;
  progress: number;
  lesson_duration: number;
  outline: CourseOutline[] | { outline: CourseOutline[]; total_hours: number };
  lessons: LessonStatus[];
  // ★ v6.68.2 (2026-07-12 老板反馈 "近期数据又丢了"):
  //   后端 SQL IN (-1, 0, 1) 让失败课程也返回, 前端用 status 区分显示
  //   1 = 已生成 (正常显示大纲), 0 = 生成中 (loading 状态), -1 = 生成失败 (红条 + 重新生成按钮)
  status?: number;
}

interface MyFormsResponse {
  code: number;
  data: {
    courses: Course[];
  };
}

interface FormItem {
  id: number;
  education_level: string;
  education_grade: string;
  age: string;
  subject: string;
  current_level: string;
  goal: string;
  expected_hours: number;
  outline: any;
  total_hours: number;
  status: number; // 0=待生成, 1=已生成, 2=生成中
  progress: number;
  lessons: LessonStatus[];
  created_at: string;
  // 【dongniai】课时时长（分钟）— 来自 [管理课程] 表单
  lesson_duration?: number;
}

interface FormsResponse {
  code: number;
  data: {
    forms: FormItem[];
  };
}

function getOutlineItems(outline: Course['outline']): CourseOutline[] {
  if (!outline) return [];
  if (Array.isArray(outline)) return outline;
  if (Array.isArray((outline as any).outline)) return (outline as any).outline;
  return [];
}

/**
 * 课时点击必须兼容两种接口数据形态：
 * 1. 旧数据/完整课程：outline_json 已保存；
 * 2. 新数据/异步生成：outline_json 可能为空，但 lesson_progress.lessons 有标题和课时信息。
 * 不允许因为 outline 为 null 就让整条“管理课程→进入课堂”链路静默 return。
 */
function getLessonItem(
  outline: Course['outline'] | FormItem['outline'],
  lessons: LessonStatus[] | undefined,
  lessonIndex: number,
): CourseOutline | null {
  const outlineItem = getOutlineItems(outline).find((item) => parseInt(item.lesson) === lessonIndex);
  if (outlineItem) return outlineItem;

  const lessonRow = (lessons || []).find((lesson) => parseInt(String(lesson.lesson_index)) === lessonIndex);
  if (!lessonRow) return null;
  return {
    lesson: String(lessonIndex),
    title: lessonRow.lesson_title || `课时${lessonIndex}`,
    hours: Number(lessonRow.lesson_hours || 1),
    description: '',
  };
}

// ★ v6.69 (2026-07-12 老板反馈「近期上课课程」大纲字号小+课程多刷不到底):
//   每页显示 15 课 + 页码翻页 + 上一/下一页按钮 (commit archaeology 显示字号 text-xs 从 6-5 起就没改, 这次是 UI 改进)
const COURSES_PAGE_SIZE = 15;

// ★ Fix 2026-06-05: 记录"最近一次点击的课程"用于"近期上课课程"Tab 置顶
const LAST_CLICKED_COURSE_KEY = 'dongniai_last_clicked_course_id';
function setLastClickedCourseId(courseId: number) {
  try {
    localStorage.setItem(LAST_CLICKED_COURSE_KEY, String(courseId));
  } catch {}
}
function getLastClickedCourseId(): number | null {
  try {
    const v = localStorage.getItem(LAST_CLICKED_COURSE_KEY);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function lessonStatusClass(status: number | string) {
  // 防御性比较：PHP 可能返回字符串 "1"（虽然 PHP 端 intval 了，但 JSON 序列化偶尔会丢类型）
  if (status == 2) return 'completed';
  if (status == 1) return 'in-progress';
  return 'not-started';
}

function lessonStatusLabel(status: number | string, progress?: number) {
  if (status == 2) return '已完成';
  if (status == 1 && progress && progress > 0) return `已学${progress}%`;
  if (status == 1) return '进行中';
  return '未学';
}

// ★ 2026-06-08：渲染"管理课程" / "近期上课课程"课时下挂的"最近学习"mini 卡片
//   根据 outlineId + lessonIndex 过滤当前课时关联的课堂
// ★ v6.81 (2026-07-12 老板反馈): 加缩略图 — 命中"最近学习"列表的 stage 有封面就显示首张幻灯片
//   缩略图容器固定宽 64px (16:9 → 36px 高),不挂 ResizeObserver (性能 + 卡片宽度不依赖它)
//   用现有 ThumbnailSlide 渲染器 (跟首页 classroom grid 同一套),保证视觉一致
function renderRecentClassroomMini(
  classrooms: StageListItem[],
  outlineId: number,
  lessonIndex: number,
  onClick: (stageId: string) => void,
  thumbnails: Record<string, Slide> = {},
) {
  const matched = classrooms.filter(c =>
    // ★ 2026-06-08：用 == 而不是 === 防御性比较（outlineId 在服务端是 int，前端可能转 string）
    c.outlineId == outlineId && c.lessonIndex == lessonIndex
  );
  if (matched.length === 0) return null;
  return (
    <div className="px-4 py-1 pb-2 bg-violet-50/40 dark:bg-violet-950/10 border-b border-black/5 dark:border-white/5">
      {matched.map(c => {
        const slide = thumbnails[c.id];
        return (
          <button
            key={c.id}
            onClick={(e) => { e.stopPropagation(); onClick(c.id); }}
            className="w-full text-left flex items-center gap-2 text-[11px] text-violet-600 dark:text-violet-300 hover:text-violet-800 dark:hover:text-violet-100 hover:underline truncate transition-colors py-0.5"
            title={c.name}
          >
            {/* 缩略图 / 占位图 */}
            {slide ? (
              <div className="shrink-0 w-16 h-9 rounded overflow-hidden bg-slate-100 dark:bg-slate-800 ring-1 ring-black/5 dark:ring-white/10">
                <ThumbnailSlide
                  slide={slide}
                  size={64}
                  viewportSize={slide.viewportSize ?? 1000}
                  viewportRatio={slide.viewportRatio ?? 0.5625}
                />
              </div>
            ) : (
              <span className="shrink-0 w-16 h-9 rounded bg-violet-100/60 dark:bg-violet-900/30 flex items-center justify-center text-base">📚</span>
            )}
            <span className="truncate flex-1">最近学习：{c.name}</span>
            {c.sceneCount > 0 && <span className="text-[10px] text-slate-400 shrink-0">({c.sceneCount}节)</span>}
          </button>
        );
      })}
    </div>
  );
}

function formStatusLabel(status: number) {
  // 防御性比较：PHP 端即使忘了 intval()，"1" == 1 也是 true（避免再被字符串状态坑）
  if (status == 1) return '已生成';
  if (status == 2) return '生成中';
  return '待生成';
}

export function CourseCatalogCard() {
  const [activeTab, setActiveTab] = useState<'courses' | 'forms'>('courses');
  const [courses, setCourses] = useState<Course[]>([]);
  const [forms, setForms] = useState<FormItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [formsLoading, setFormsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  // ★ v6.97.x (2026-07-14): 登录态守卫 — 订阅 useAuthStore 让组件响应登录/登出
  //   老板反馈: 未登录首页右侧也显示「近期上课课程」「管理课程」Tab + 空状态
  //   参考实现: /dongniai/plan/myForms (PHP 直出) 未登录显示「请先登录」登录墙
  //   这里在客户端组件层做同样的守卫, 不影响 wrapper 拖拽/全屏/位置持久化
  const loggedIn = useAuthStore((s) => s.loggedIn);
  const checked = useAuthStore((s) => s.checked);
  // 展开/折叠：记录哪些 form 的课时列表处于展开状态
  const [expandedForms, setExpandedForms] = useState<Set<number>>(new Set());
  // ★ 2026-06-08：从 IndexedDB 拉"最近学习"课堂，用于课时下挂 mini 卡片
  const [classrooms, setClassrooms] = useState<StageListItem[]>([]);
  // ★ v6.69 (2026-07-12): 「近期上课课程」课程大纲分页当前页码 (1-based, 默认第 1 页)
  const [coursesPage, setCoursesPage] = useState(1);
  // ★ v6.69: 「管理课程」Tab 每张 form 卡片的大纲分页当前页码 (formId → 1-based pageNum)
  const [formPages, setFormPages] = useState<Record<number, number>>({});
  // ★ v6.81 (2026-07-12 老板反馈): 给 "管理课程" 课时下的"最近学习" mini 卡片
  //   加缩略图:每个匹配的 stage 取首张幻灯片的 canvas + media,渲染成 16:9 小缩略图
  //   数据源跟首页 classroom grid 同源 (getFirstSlideByStages) — 不存服务端,纯客户端
  const [recentThumbnails, setRecentThumbnails] = useState<Record<string, Slide>>({});

  useEffect(() => {
    // 拉最近学习课堂
    listStages()
      .then(async items => {
        setClassrooms(items);
        // ★ v6.81 同时拉所有"最近学习"课堂的首页封面 (供管理课程课时下挂的 mini 卡片用)
        if (items.length > 0) {
          try {
            const slides = await getFirstSlideByStages(items.map(c => c.id));
            setRecentThumbnails(slides);
          } catch (e) {
            // 缩略图拉失败不阻断整体 (mini 卡片会 fallback 到 📚 emoji 占位)
          }
        }
      })
      .catch(() => setClassrooms([]));
  }, []);

  const toggleFormExpand = (formId: number) => {
    setExpandedForms(prev => {
      const next = new Set(prev);
      if (next.has(formId)) next.delete(formId); else next.add(formId);
      return next;
    });
  };


  useEffect(() => {
    // ★ v6.97.x (2026-07-14 老板反馈): 修复「登录后有时显示有时很久不显示」+ 「未登录也显示 Tab」
    //   旧逻辑:
    //     - fetch('/api/my-courses') 在组件 mount 时立即发起 (依赖 [])
    //     - 但首页另一个 useEffect 也在跑 useAuthStore.fetchStatus() (依赖 [])
    //     - 两个 effect 哪个先完成是 race condition:
    //       a) 如果 courses fetch 先发, auth 还没确认, fetch 带 cookie (credentials:include)
    //          拿到数据 → setCourses → 一切正常
    //       b) 如果 courses fetch 后到, 但 cookie 还没生效 (FastAdmin Set-Cookie 同步延迟) →
    //          API 返 code:0 '请先登录' → setCourses 不调用 → UI 卡"暂无课程"永远不刷新
    //       c) 用户从其他页面登录后跳到首页, cookie 已设置但 courses fetch 已结束 → 卡空状态
    //   新逻辑:
    //     - 依赖 loggedIn + checked, auth 状态变化时自动重新 fetch
    //     - 等 auth checked 完成再 fetch (避免 race)
    //     - 未登录时不 fetch, 直接渲染登录墙 (Bug 1 修复)
    if (!checked) return;
    if (!loggedIn) {
      // 未登录: 清空状态 + 结束 loading, 让登录墙 UI 接管渲染
      setCourses([]);
      setForms([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch('/api/my-courses', { credentials: 'include' })
      .then(r => r.json())
      .then((res: MyFormsResponse) => {
        if (cancelled) return;
        if (res.code === 1 && res.data && Array.isArray(res.data.courses)) {
          setCourses(res.data.courses);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loggedIn, checked]);

  const loadForms = () => {
    if (forms.length > 0) return;
    setFormsLoading(true);
    fetch('/api/my-forms', { credentials: 'include' })
      .then(r => r.json())
      .then((res: FormsResponse) => {
        if (res.code === 1 && Array.isArray(res.data?.forms)) {
          setForms(res.data.forms);
        }
      })
      .catch(() => {})
      .finally(() => setFormsLoading(false));
  };

  const handleTabChange = (tab: 'courses' | 'forms') => {
    setActiveTab(tab);
    if (tab === 'forms') loadForms();
  };

  // ★ 2026-06-08：点击"管理课程"课时下的"最近学习" mini 卡片 → 直接跳到 classroom
  //   不再走"重新进入 prompt"路径（因为已有 stageId，直接进 classroom 即可）
  const handleRecentClassroomClick = (stageId: string) => {
    if (typeof window !== 'undefined') {
      window.location.href = `/classroom/${stageId}`;
    }
  };

  // 点击课时 → 派发事件填入提示词到输入框（生成 dongniai 课程格式）
  const handleLessonClick = (courseId: number, lessonIndex: number) => {
    const course = courses.find(c => c.id === courseId);
    if (!course) return;
    // ★ 2026-09-09 修「近期上课课程第几课点击没反应」P0 — getLessonItem 双源 fallback (outline→lessons)
    const item = getLessonItem(course.outline, course.lessons, lessonIndex);
    if (!item) {
      console.warn('[handleLessonClick] lesson not found', { courseId, lessonIndex, hasOutline: !!course.outline, lessonsCount: course.lessons?.length });
      alert(`未找到第 ${lessonIndex} 课数据，请重新生成课程后再试。`);
      return;
    }

    // ★ Fix 2026-06-05: 记录最近一次点击的课程，让"近期上课课程"Tab 切到这节课
    setLastClickedCourseId(courseId);

    const prompt = `【课程基本信息】
[科目] ${course.subject || 'AI课程'}
[学生教育阶段] ${course.education_level}${course.education_grade}
[学生基础水平] ${course.current_level}
[学生学习目标] ${course.goal}
[课时时长] ${course.lesson_duration ? course.lesson_duration + '分钟' : '60分钟'}

【课堂流程】
请根据上述信息，设计本节课的完整【课堂流程】。
课堂流程应包含：开场引入、概念讲解、实例分析、师生互动、总结回顾等环节，并说明每个环节的时间分配和教学设计意图。

【本节课内容】
请根据上述【课堂流程】，生成第${item.lesson}课「${item.title}」的详细内容：
- 本课核心知识点及重难点
- 知识点的讲解逻辑（由浅入深的具体讲解步骤）
- 精选例题及详细解题步骤
- 配套练习题
- 板书/白板规划（建议在白板上画什么、写什么）

【教学要求】
请根据上述信息，明确本节课的教学要求：
- 学生应掌握的核心知识和技能
- 教学重点与难点
- 建议的教学方法和互动策略
- 课后巩固建议`;

    // ★ 传 outlineId + lessonIndex，供 page.tsx 存 localStorage → generation-preview 创建课堂后回写 lesson_progress
    window.dispatchEvent(new CustomEvent('fill-course-prompt', { detail: { prompt, outlineId: courseId, lessonIndex }, bubbles: true }));
  };

  // 点击管理课程卡片中的课时 → 填入完整提示词（生成 dongniai 课程格式）
  const handleFormLessonClick = (formId: number, lessonIndex: number) => {
    const form = forms.find(f => f.id === formId);
    if (!form) return;
    // ★ 2026-09-09 修「管理课程第几课点击没反应」P0 — getLessonItem 已加 outline+lessons 双源 fallback
    //   (生产源码 9-7 已加，但 build 一直没刷新, 今天重新 build 让生效)
    const item = getLessonItem(form.outline, form.lessons, lessonIndex);
    if (!item) {
      // ★ 兜底 UX: 即使 outline + lessons 都找不到 lesson, 也不静默 return
      //   提示用户去后台审计/重新生成, 而不是盯着屏幕不知道发生了什么
      console.warn('[handleFormLessonClick] lesson not found', { formId, lessonIndex, hasOutline: !!form.outline, lessonsCount: form.lessons?.length });
      alert(`未找到第 ${lessonIndex} 课数据，请重新生成课程后再试。`);
      return;
    }

    // ★ Fix 2026-06-05: 记录最近一次点击的课程，让"近期上课课程"Tab 切到这节课
    setLastClickedCourseId(formId);

    const prompt = `【课程基本信息】
[科目] ${form.subject || 'AI课程'}
[学生教育阶段] ${form.education_level}${form.education_grade}
[学生基础水平] ${form.current_level}
[学生学习目标] ${form.goal}
[课时时长] ${form.lesson_duration ? form.lesson_duration + '分钟' : '60分钟'}

【课堂流程】
请根据上述信息，设计本节课的完整【课堂流程】。
课堂流程应包含：开场引入、概念讲解、实例分析、师生互动、总结回顾等环节，并说明每个环节的时间分配和教学设计意图。

【本节课内容】
请根据上述【课堂流程】，生成第${item.lesson}课「${item.title}」的详细内容：
- 本课核心知识点及重难点
- 知识点的讲解逻辑（由浅入深的具体讲解步骤）
- 精选例题及详细解题步骤
- 配套练习题
- 板书/白板规划（建议在白板上画什么、写什么）

【教学要求】
请根据上述信息，明确本节课的教学要求：
- 学生应掌握的核心知识和技能
- 教学重点与难点
- 建议的教学方法和互动策略
- 课后巩固建议`;

    // ★ 传 outlineId + lessonIndex，供 page.tsx 存 localStorage → generation-preview 创建课堂后回写 lesson_progress
    window.dispatchEvent(new CustomEvent('fill-course-prompt', { detail: { prompt, outlineId: formId, lessonIndex }, bubbles: true }));
  };

  const handleRegenerate = async (formId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('确定要重新生成这门课程吗？这将覆盖原有的课程内容。')) return;
    setActionLoading(formId);
    try {
      const res = await fetch('/api/my-forms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: formId }),
      });
      const data = await res.json();
      if (data.code === 1) {
        alert('重新生成成功！');
        loadForms();
      } else {
        alert('重新生成失败：' + (data.msg || '未知错误'));
      }
    } catch {
      alert('重新生成失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (formId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('确定要删除这个课程吗？')) return;
    setActionLoading(formId);
    try {
      const res = await fetch(`/api/my-forms?id=${formId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.code === 1) {
        setForms(prev => prev.filter(f => f.id !== formId));
      } else {
        alert('删除失败：' + (data.msg || '未知错误'));
      }
    } catch {
      alert('删除失败');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-48 flex items-center justify-center">
        <div className="animate-spin w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // ============================================================
  // 近期上课课程 Tab — 完全复刻 myForms.html 侧边栏样式
  // ============================================================
  const renderCoursesTab = () => {
    if (courses.length === 0) {
      return (
        <div className="text-center py-10 px-4">
          <div className="text-4xl mb-3 opacity-40">📚</div>
          <p className="text-sm text-violet-400/60 font-medium">暂无课程</p>
          <small className="text-xs text-violet-400/40">填写表单后，AI将为您生成专属课程</small>
        </div>
      );
    }

    // ★ Fix 2026-06-05: 按"最近一次点击的课程"置顶；没记录就保留后端原排序
    //   后端默认按 last_study_time DESC（即"最近学习的"在前）。
    // ★ Fix v2 2026-06-05: /api/my-courses 返回的 id 是 string（如 "34"），
    //   getLastClickedCourseId() 用 parseInt 返回 number。
    //   必须用 Number() 强转再比较，否则永远 false。
    const lastClickedId = getLastClickedCourseId();
    const sortedCourses = lastClickedId
      ? (() => {
          const hit = courses.find(c => Number(c.id) === lastClickedId);
          if (!hit) return courses;
          const rest = courses.filter(c => Number(c.id) !== lastClickedId);
          return [hit, ...rest];
        })()
      : courses;

    // 取最近一门课程（完全照搬 HTML 逻辑）
    const course = sortedCourses[0];
    let outlineItems = getOutlineItems(course.outline);

    // ★ P0 修复 2026-09-07: outline_json=NULL 时也能显示大纲
    //   真因: 9-3 老板 P0 报"管理课程→具体课时→近期上课课程 没进去",
    //         真因之一是 course_outline.outline_json=NULL 但 lesson_progress.lessons[] 有数据
    //         旧代码 outlineItems=[] 直接渲染"暂无大纲" → 看不到任何课时
    //   修法: outline 为空但 lessons[] 有数据时, 用 lessons[] 合成虚拟大纲
    //         (跟 getLessonItem() helper 同款逻辑, 保持单一可信源)
    if (outlineItems.length === 0 && Array.isArray(course.lessons) && course.lessons.length > 0) {
      outlineItems = (course.lessons as LessonStatus[]).map(l => ({
        lesson: String(l.lesson_index),
        title: l.lesson_title || `课时${l.lesson_index}`,
        hours: Number(l.lesson_hours || 1),
        description: '',
      }));
    }

    // ★ v6.69 (2026-07-12 老板反馈): 分页 — 每页 15 课, 显示页码 + 上一/下一页按钮
    const totalPages = Math.max(1, Math.ceil(outlineItems.length / COURSES_PAGE_SIZE));
    const safePage = Math.min(Math.max(1, coursesPage), totalPages);
    const startIdx = (safePage - 1) * COURSES_PAGE_SIZE;
    const pageItems = outlineItems.slice(startIdx, startIdx + COURSES_PAGE_SIZE);

    // ★ v6.78 (2026-07-12 老板反馈): key prop 包含分页符, 让 React 重渲染时分页切换不报错
    const outlineListKey = `outline-${course.id}-${safePage}`;

    const lessonStatusMap: Record<number, number> = {};
    const lessonProgressMap: Record<number, number> = {};
    (course.lessons || []).forEach((l: LessonStatus) => { lessonStatusMap[l.lesson_index] = l.status; lessonProgressMap[l.lesson_index] = l.progress ?? 0; });

    return (
      <div className="flex flex-col h-full">
        {/* ★ v6.68.2: status=-1 (生成失败) 时头部改红色 + 显示「生成失败」徽章 */}
        <div
          className={`px-4 py-3.5 cursor-pointer shrink-0 ${
            course.status === -1
              ? 'bg-gradient-to-r from-red-500 to-rose-500'
              : 'bg-gradient-to-r from-violet-500 to-purple-500'
          }`}
          onClick={() => {
            // ★ Fix 2026-06-05: 顶部卡片点击也记一次"最近点击的课程"
            setLastClickedCourseId(course.id);
            window.location.href = `/dongniai/?outline_id=${course.id}&lesson=1`;
          }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white/90 truncate">
              {course.education_level}{course.education_grade} · {course.subject}
            </span>
            <span className="text-xs text-white/80 shrink-0 ml-2 px-2 py-0.5 rounded-full bg-white/20">
              {/* ★ v6.68.2: 状态徽章 */}
              {course.status === -1 ? '⚠️ 生成失败' : course.status === 0 ? '⏳ 生成中...' : (course.progress && course.progress > 0 ? `已学${course.progress}%` : '未开始')}
            </span>
          </div>
          {/* 元信息行 */}
          <div className="mt-1.5 text-xs text-white/55 flex flex-wrap gap-x-2 gap-y-0.5">
            <span>基础：{course.current_level}</span>
            <span>·</span>
            <span>总课时：{course.total_hours || outlineItems.length || 0}课时</span>
            {course.goal && (
              <>
                <span>·</span>
                <span className="truncate max-w-[120px]">目标：{course.goal}</span>
              </>
            )}
          </div>
        </div>

        {/* 课程大纲标题 — 字号放大 text-xs → text-sm */}
        <div className="px-4 pt-3 pb-1.5 text-sm font-semibold text-violet-500 dark:text-violet-400 tracking-wide shrink-0">
          📋 课程大纲（共{outlineItems.length}课{outlineItems.length > COURSES_PAGE_SIZE ? `，第 ${safePage}/${totalPages} 页` : ''}）
        </div>

        {/* ★ v6.68.2: status=-1 时显示「生成失败」提示 + 重新生成按钮 */}
        {course.status === -1 ? (
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-6 bg-red-50/40 dark:bg-red-950/10">
            <div className="text-4xl">⚠️</div>
            <div className="text-sm font-medium text-red-700 dark:text-red-300">AI 生成失败</div>
            <div className="text-xs text-red-600/70 dark:text-red-400/60 text-center max-w-[240px]">
              LLM 服务暂时不可用 (minimax 5h rate limit 等), 课程大纲暂未生成
            </div>
            <button
              type="button"
              disabled={actionLoading === course.id}
              onClick={async () => {
                if (!confirm('确定要重新生成这门课程吗？')) return;
                setActionLoading(course.id);
                try {
                  const res = await fetch('/api/my-forms', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ id: course.id }),
                  });
                  const data = await res.json();
                  if (data.code === 1) {
                    alert('重新生成成功！正在刷新...');
                    // 重新 fetch courses
                    const r = await fetch('/api/my-courses', { credentials: 'include' });
                    const j = await r.json();
                    if (j.code === 1 && Array.isArray(j.data?.courses)) {
                      setCourses(j.data.courses);
                    }
                  } else {
                    alert('重新生成失败：' + (data.msg || '未知错误'));
                  }
                } catch {
                  alert('重新生成失败');
                } finally {
                  setActionLoading(null);
                }
              }}
              className="text-sm px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-40 flex items-center gap-1.5 transition-colors"
            >
              {actionLoading === course.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              重新生成
            </button>
          </div>
        ) : pageItems.length > 0 ? (
          <div key={outlineListKey} className="flex-1 min-h-0 overflow-y-auto">
            {pageItems.map(item => {
              const idx = parseInt(item.lesson) || 0;
              const status = lessonStatusMap[idx] ?? 0;
              const sc = lessonStatusClass(status);
              return (
                <div key={idx}>
                  <div
                    className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all hover:translate-x-1 text-sm ${sc === 'completed' ? 'bg-emerald-50 dark:bg-emerald-950/30' : sc === 'in-progress' ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-orange-50 dark:bg-orange-950/20'} border-b border-black/5 dark:border-white/5`}
                    onClick={() => handleLessonClick(course.id, idx)}
                  >
                    <span className={`flex-1 truncate pr-2 ${sc === 'completed' ? 'text-emerald-600/70 dark:text-emerald-400/70' : 'text-slate-700 dark:text-slate-300'}`}>
                      第{item.lesson}课：{item.title || ('课时' + item.lesson)}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold shrink-0 ${sc === 'completed' ? 'bg-emerald-500 text-white' : sc === 'in-progress' ? 'bg-blue-500 text-white' : 'bg-orange-400 text-white'}`}>
                      {lessonStatusLabel(status, lessonProgressMap[idx])}
                    </span>
                  </div>
                  {/* ★ 2026-06-08：该课时下挂的"最近学习"课堂（按 outlineId + lessonIndex 关联） */}
                  {/* ★ v6.81 加缩略图 */}
                  {renderRecentClassroomMini(classrooms, course.id, idx, handleRecentClassroomClick, recentThumbnails)}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400">暂无大纲</div>
        )}

        {/* ★ v6.69 分页器 — 仅当课程总数 > 15 时显示 (页码 + 上一/下一页按钮) */}
        {outlineItems.length > COURSES_PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-violet-100/60 dark:border-violet-800/60 bg-violet-50/40 dark:bg-violet-950/20 shrink-0">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setCoursesPage(p => Math.max(1, p - 1))}
              className="text-xs px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ‹ 上一页
            </button>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setCoursesPage(pageNum)}
                  className={`text-xs min-w-[28px] px-2 py-1.5 rounded-lg transition-colors ${
                    pageNum === safePage
                      ? 'bg-violet-500 text-white font-semibold'
                      : 'bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={safePage === totalPages}
              onClick={() => setCoursesPage(p => Math.min(totalPages, p + 1))}
              className="text-xs px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              下一页 ›
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // 管理课程 Tab — 完全复刻 myForms.html 表单卡片样式
  // ============================================================
  const renderFormsTab = () => {
    if (formsLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
        </div>
      );
    }

    // ★ v6.97.38.2 (2026-07-16 老板反馈 P2): 严格过滤 — 管理课程 Tab 只显示「已生成」
    //   v6.97.38.1 用 status != 0, 保留 status==-1 (生成失败) 卡片。但 line 689 badge
    //   fallback 把 -1 误标成「待生成」, 老板以为 filter 没生效。
    //   v6.97.38.2 改成严格 status == 1, 跟老板字面要求「只显示已生成」对齐:
    //     - status=0 (待生成): 隐藏 ✓
    //     - status=-1 (生成失败): 也隐藏 (因为没正确 label, 留在这会误导) —
    //       触发重新生成的 path: POST /api/my-forms?id=<formId> 或后台手动
    //     - status=2 (生成中): 也隐藏 — 罕见瞬态, AI 跑完就会跳到 status=1
    //   防御: 下面 § badge label 同步修成 -1 → '生成失败', 万一以后放宽 filter 不会再误标。
    //   用 == 不是 ===, 防御 PHP 又把 int 退回成 string (跟 line 175 formStatusLabel 同款)。
    const visibleForms = forms.filter(f => f.status == 1);

    if (visibleForms.length === 0) {
      return (
        <div className="text-center py-10 px-4">
          <div className="text-4xl mb-3 opacity-40">📋</div>
          <p className="text-sm text-violet-400/60 font-medium">暂无已生成的课程</p>
          <small className="text-xs text-violet-400/40">通过"AI生成课程"后，规划信息将显示在这里</small>
        </div>
      );
    }

    return (
      // ★ v6.78 (2026-07-12): flex:1 + min-h:0 + overflow-y-auto 跟 outer Tab content 协作,
      // 跟随 sidebar 拉到的高度走 (不再 max-h-[480px] 写死导致底部空白)
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="grid grid-cols-1 gap-4">
          {visibleForms.map(form => {
            // ★ Fix 2026-06-05: 同样改成 == 防御性比较（防 PHP 又退回字符串）
            // ★ v6.97.38.2 (2026-07-16): 同步修 pre-existing badge bug — 旧代码
            //   `form.status == 1 ? '已生成' : form.status == 2 ? '生成中' : '待生成'`
            //   fallback 把 -1 (生成失败) 误标成"待生成"。补一条 -1 → '生成失败'。
            //   防御性: 即使未来 filter 放宽, label 也不会再误导。
            const statusText = form.status == 1 ? '已生成' : form.status == 2 ? '生成中' : form.status == -1 ? '生成失败' : '待生成';
            const progressText = form.progress && form.progress > 0 ? `已学${form.progress}%` : '未学习';
            const statusBg = form.status == 1 ? 'bg-emerald-500' : form.status == 2 ? 'bg-amber-500' : form.status == -1 ? 'bg-red-500' : 'bg-slate-400';
            return (
              <div key={form.id} className="rounded-xl border border-violet-100 dark:border-violet-800 overflow-hidden bg-white dark:bg-slate-800/50 transition-all hover:shadow-md hover:border-violet-300 dark:hover:border-violet-600">
                {/* 卡片头部 — 科目名 + 删除按钮同排 */}
                <div className="px-4 py-3 bg-gradient-to-r from-violet-500 to-purple-500">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-white truncate">
                      {form.education_level}{form.education_grade ? ' ' + form.education_grade : ''} · {form.subject}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(form.id, e); }}
                      className="shrink-0 ml-2 p-1 rounded text-white/50 hover:text-white hover:bg-white/20 transition-colors"
                      title="删除课程"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-white/60">基础：{form.current_level}</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold text-white ${statusBg}`}>{statusText}</span>
                      <span className="text-[10px] text-white/50">{progressText}</span>
                    </div>
                  </div>
                </div>

                {/* 卡片信息 */}
                <div className="px-4 py-3 bg-violet-50/60 dark:bg-violet-950/20">
                  <div className="text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
                    {form.goal && <p>🎯 目标：{form.goal}</p>}
                    <p>📚 期望 {form.expected_hours || '?'} 节课</p>
                    {form.lesson_duration ? <p>⏱️ 每节课时长：{form.lesson_duration} 分钟</p> : <p>⏱️ 每节课时长：60 分钟</p>}
                  </div>

                  {/* 进度条 */}
                  {form.progress > 0 && (
                    <div className="mt-2 bg-violet-100 dark:bg-violet-900/40 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all"
                        style={{ width: `${form.progress}%` }}
                      />
                    </div>
                  )}

                  {/* 课时列表 — v6.69 字号放大 + 每 15 课分页(页码 + 上一/下一页按钮) */}
                  {form.lessons && form.lessons.length > 0 && (() => {
                    const totalLessons = form.lessons.length;
                    const totalPages = Math.max(1, Math.ceil(totalLessons / COURSES_PAGE_SIZE));
                    const currentPage = Math.min(Math.max(1, formPages[form.id] || 1), totalPages);
                    const startIdx = (currentPage - 1) * COURSES_PAGE_SIZE;
                    const pageLessons = form.lessons.slice(startIdx, startIdx + COURSES_PAGE_SIZE);
                    return (
                      <>
                      <div className="mt-2.5 rounded-lg border border-violet-100 dark:border-violet-900/50 overflow-y-auto">
                        {pageLessons.map(lesson => {
                          const sc = lessonStatusClass(lesson.status);
                          return (
                            <div key={lesson.lesson_index}>
                              <div
                                className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-all hover:translate-x-1 border-b border-black/5 dark:border-white/5 last:border-b-0 ${sc === 'completed' ? 'bg-emerald-50 dark:bg-emerald-950/30' : sc === 'in-progress' ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-orange-50 dark:bg-orange-950/20'}`}
                                onClick={() => handleFormLessonClick(form.id, Number(lesson.lesson_index))}
                              >
                                <span className={`truncate pr-2 ${sc === 'completed' ? 'text-emerald-600/70 dark:text-emerald-400/70' : 'text-slate-600 dark:text-slate-300'}`}>
                                  第{lesson.lesson_index}课：{lesson.lesson_title || ('课时' + lesson.lesson_index)}
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${sc === 'completed' ? 'bg-emerald-500 text-white' : sc === 'in-progress' ? 'bg-blue-500 text-white' : 'bg-orange-400 text-white'}`}>
                                  {lessonStatusLabel(lesson.status, lesson.progress)}
                                </span>
                              </div>
                              {/* ★ 2026-06-08：管理课程课时下挂"最近学习" mini 卡片 */}
                              {/* ★ v6.81 加缩略图 */}
                              {renderRecentClassroomMini(classrooms, form.id, Number(lesson.lesson_index), handleRecentClassroomClick, recentThumbnails)}
                            </div>
                          );
                        })}
                      </div>
                      {/* ★ v6.69 分页器 — 仅当课程总数 > 15 时显示 (页码 + 上一/下一页按钮) */}
                      {totalLessons > COURSES_PAGE_SIZE && (
                        <div className="mt-2 flex items-center justify-between gap-2 px-2 py-2 rounded-lg bg-violet-50/60 dark:bg-violet-950/20 border border-violet-100/60 dark:border-violet-800/60">
                          <button
                            type="button"
                            disabled={currentPage === 1}
                            onClick={() => setFormPages(prev => ({ ...prev, [form.id]: Math.max(1, currentPage - 1) }))}
                            className="text-[11px] px-2 py-1 rounded-md bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            ‹ 上一页
                          </button>
                          <div className="flex items-center gap-1 flex-wrap justify-center">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                              <button
                                key={pageNum}
                                type="button"
                                onClick={() => setFormPages(prev => ({ ...prev, [form.id]: pageNum }))}
                                className={`text-[11px] min-w-[24px] px-1.5 py-1 rounded-md transition-colors ${
                                  pageNum === currentPage
                                    ? 'bg-violet-500 text-white font-semibold'
                                    : 'bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40'
                                }`}
                              >
                                {pageNum}
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            disabled={currentPage === totalPages}
                            onClick={() => setFormPages(prev => ({ ...prev, [form.id]: Math.min(totalPages, currentPage + 1) }))}
                            className="text-[11px] px-2 py-1 rounded-md bg-white dark:bg-slate-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            下一页 ›
                          </button>
                        </div>
                      )}
                      </>
                    );
                  })()}
                </div>

                {/* 操作按钮 */}
                <div className="px-4 py-2.5 flex gap-2 border-t border-violet-100/60 dark:border-violet-800/60">
                  {/* ★ v6.97.38.2 (2026-07-16): 生成失败 (-1) 也给"重新生成"按钮, 防御 filter 放宽后 user 能补救 */}
                  {form.status === 0 || form.status === 2 || form.status === -1 ? (
                    <button
                      disabled={form.status === 2}
                      onClick={(e) => handleRegenerate(form.id, e)}
                      className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-amber-400 hover:bg-amber-500 text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
                    >
                      {actionLoading === form.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      重新生成
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    // ★ v6.77 (2026-07-12 老板反馈): 拉长 sidebar 后底部空白
    //   原因: Card 顶层 div 无高度, 高度 = 内容自然高度
    //   修法: h-full + flex flex-col 让 Card 填满父级 (sidebar inner),
    //         下方 tab content 用 flex:1 + overflow:auto 自动滚动
    //   备: 加 inline style 强制 height:100% (Tailwind v4 production build
    //         偶尔会 purge h-full, inline style 保证生效)
    // ★ v6.97.x (2026-07-14): 登录态守卫 — 未登录显示登录墙 (跟 myForms 一致),
    //   不渲染 Tab 区域, 避免误导用户以为有课程数据。
    //   守卫检查放在所有 hooks 之后, 遵守 React hooks rules-of-order
    <div
      className="flex flex-col bg-white/80 dark:bg-slate-900/80 rounded-2xl border border-violet-100 dark:border-violet-800/60 overflow-hidden"
      style={{ flex: '1 1 auto', minHeight: 0 }}
    >
      {/* ★ v6.97.x: 登录态分支 — 未登录渲染登录墙, 已登录渲染 Tab + 内容 */}
      {!loggedIn ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/40 dark:to-indigo-900/40 flex items-center justify-center">
            <LogIn className="w-7 h-7 text-violet-500 dark:text-violet-300" />
          </div>
          <div className="text-sm font-semibold text-violet-700 dark:text-violet-200">
            请先登录
          </div>
          <div className="text-xs text-violet-500/70 dark:text-violet-400/60 max-w-[220px] leading-relaxed">
            登录后可查看您的近期课程与管理课程
          </div>
          {/* 【v6.97.85+11 P0 修复 2026-09-02 老板报】从卡片上面点"立即登录"按钮跳到 /dashboard?ref=addtabs
              真因: window.location.href = '/login' (相对路径) 在 basePath=/dongniai 下被浏览器解析为
                https://www.xiaoquanwk.com/login → nginx 兜底 @fastadmin → KoOIsYnLPb.php?_url=/login
                → FastAdmin 检测无 addtabs 标记 → 重定向 /dashboard?ref=addtabs (老板看到的现象)
              修法: 按 v6.97.83+5 模板升级为 window.location.origin + 绝对路径,
                绕开 nginx @fastadmin 兜底段 + 直接命中 location = /index/user/login.html PHP 登录页 */}
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = window.location.origin + '/index/user/login.html?url=/dongniai/';
              }
            }}
            className="mt-2 inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-medium shadow-sm transition-colors"
          >
            <LogIn className="w-3.5 h-3.5" />
            前往登录
          </button>
        </div>
      ) : null}

      {/* 已登录: 原 Tab 区域 — 但要避免 SSR/hydration mismatch, 加 suppressHydrationWarning */}
      {loggedIn ? (
      <>
      {/* Tab bar */}
      <div className="flex gap-1 px-3 pt-3 pb-0 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 border-b border-violet-100/60 dark:border-violet-800/60">
        <button
          onClick={() => handleTabChange('courses')}
          className={`flex-1 py-2.5 text-xs font-medium rounded-t-xl transition-all ${activeTab === 'courses' ? 'bg-white dark:bg-slate-800 text-violet-600 dark:text-violet-300 shadow-sm' : 'text-muted-foreground/60 hover:text-violet-500'}`}
        >
          <BookOpen className="w-3.5 h-3.5 inline mr-1" />
          近期上课课程
        </button>
        <button
          onClick={() => handleTabChange('forms')}
          className={`flex-1 py-2.5 text-xs font-medium rounded-t-xl transition-all ${activeTab === 'forms' ? 'bg-white dark:bg-slate-800 text-violet-600 dark:text-violet-300 shadow-sm' : 'text-muted-foreground/60 hover:text-violet-500'}`}
        >
          <Sparkles className="w-3.5 h-3.5 inline mr-1" />
          管理课程
        </button>
      </div>

      {/* Tab content - ★ v6.78 flex:1 撑开父级, 不要自身 overflow (让 inner 滚, inner 自己算高度) */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {activeTab === 'courses' ? renderCoursesTab() : renderFormsTab()}
      </div>
      </>
      ) : null}
    </div>
  );
}
